import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Star, StarHalf } from 'lucide-react';
import MagneticButton from './MagneticButton';

export default function Hero() {
    const containerRef = useRef(null);
    const bgRef = useRef(null);

    // Text to split
    const heading1 = "Szczelny Dach Płaski na Lata.";
    const heading2 = "Mistrzowie w Papie.";

    useEffect(() => {
        let ctx = gsap.context(() => {
            
            // Staggered reveal for individual characters
            gsap.from(".char-reveal", {
                y: 100,
                opacity: 0,
                duration: 1.2,
                stagger: 0.02,
                ease: "power4.out",
                delay: 0.1
            });

            // Reveal for non-split elements (tags, paragraphs, buttons)
            gsap.from(".reveal-element", {
                y: 40,
                opacity: 0,
                duration: 1.2,
                stagger: 0.08,
                ease: "power3.out",
                delay: 0.6 // Wait for chars to mostly finish
            });

            // Parallax effect for the background image
            gsap.to(bgRef.current, {
                y: '30%', // Move background down by 30% of its height as we scroll down
                ease: "none",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top top",
                    end: "bottom top",
                    scrub: true
                }
            });

        }, containerRef);

        return () => ctx.revert();
    }, []);

    // Helper to wrap characters in spans, ensuring words don't break across lines
    const splitTextToChars = (text, addDramaClass = false) => {
        return text.split(' ').map((word, wordIndex) => (
            <span key={`word-${wordIndex}`} className="inline-flex shrink-0 whitespace-nowrap mr-[0.3em] overflow-visible">
                {word.split('').map((char, charIndex) => (
                    <span 
                        key={`char-${wordIndex}-${charIndex}`} 
                        className={`char-reveal inline-block ${addDramaClass ? 'drama-text' : ''}`}
                        style={{ willChange: 'transform, opacity' }}
                    >
                        {char}
                    </span>
                ))}
            </span>
        ));
    };

    return (
        <section
            ref={containerRef}
            className="relative w-full min-h-[100dvh] flex flex-col justify-end overflow-hidden pt-36 pb-12 md:pb-16 px-6 md:px-16 bg-dark"
        >
            {/* Parallax Background Layer */}
            <div 
                ref={bgRef}
                className="absolute top-0 left-0 w-full h-[120%] bg-cover bg-center pointer-events-none z-0 brightness-75"
                style={{ backgroundImage: "url('/warehouse-tar-roof.webp')", willChange: 'transform' }}
            ></div>

            {/* The Top-Heavy Dark Gradient for Text Contrast */}
            <div className="absolute inset-x-0 top-0 h-[60%] bg-gradient-to-b from-black/70 to-transparent pointer-events-none z-10"></div>

            {/* Przyciemnienie od dołu — czytelność nad kolejną sekcją */}
            <div className="absolute inset-x-0 bottom-0 h-[65%] bg-gradient-to-t from-background via-background/85 to-transparent pointer-events-none z-10"></div>

            <div className="relative z-20 w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-end justify-between gap-12 lg:gap-8">

                {/* Content Pushed to Bottom Left */}
                <div className="w-full lg:w-2/3 xl:w-1/2 flex flex-col items-start mt-32 lg:mt-0">

                    {/* Badges Container */}
                    <div className="reveal-element flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                        
                        {/* Fixly Trust Badge */}
                        <a href="#opinie" className="flex items-center space-x-2 bg-[#00c853]/90 hover:bg-[#00c853] transition-colors backdrop-blur-md px-4 py-2 rounded-full border border-[#00c853]/50 shadow-[0_0_20px_rgba(0,200,83,0.3)] cursor-pointer group">
                            <div className="flex gap-0.5 text-white">
                                <Star size={14} className="fill-current" />
                                <Star size={14} className="fill-current" />
                                <Star size={14} className="fill-current" />
                                <Star size={14} className="fill-current" />
                                <StarHalf size={14} className="fill-current" />
                            </div>
                            <span className="font-mono text-[11px] md:text-xs text-white uppercase tracking-widest font-bold">
                                4.6/5 na Fixly (14 opinii)
                            </span>
                        </a>

                        {/* Location Badge */}
                        <div className="flex items-center space-x-3 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-sm hidden sm:flex">
                            <div className="w-2 h-2 rounded-full bg-accent animate-pulse"></div>
                            <span className="font-mono text-[11px] md:text-xs text-white uppercase tracking-widest font-semibold">
                                📍 Śląsk
                            </span>
                        </div>
                    </div>

                    {/* Massive Typography matching Memory format - Now with character splits */}
                    <h1 className="flex flex-col mb-8 pointer-events-none">
                        <span className="text-[#F8F8F8] font-heading font-black tracking-tighter text-[clamp(2.5rem,5vw,5rem)] leading-[0.9] uppercase overflow-hidden flex flex-wrap drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                            {splitTextToChars(heading1)}
                        </span>
                        <span className="text-accent text-[clamp(2rem,4vw,4rem)] mt-2 overflow-hidden flex flex-wrap drop-shadow-[0_2px_10px_rgba(0,0,0,0.3)]" style={{ lineHeight: "1.1" }}>
                            {splitTextToChars(heading2, true)}
                        </span>
                    </h1>

                    <p className="reveal-element text-[#F8F8F8]/90 font-body text-lg md:text-xl max-w-lg leading-relaxed mb-10 drop-shadow-[0_2px_5px_rgba(0,0,0,0.5)] font-medium">
                        Specjaliści od dachów płaskich. Oferujemy kompleksowe wykonawstwo poszyć, głównie z papy termozgrzewalnej i blachy. Profesjonalne remonty, hydroizolacje i termomodernizacja dachów.
                    </p>

                    <div className="reveal-element flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto">
                        <MagneticButton
                            href="#kontakt"
                            className="bg-accent text-background font-heading font-bold text-lg px-10 py-5 rounded-full w-[260px] uppercase tracking-widest hover:bg-accent/90"
                        >
                            <span>Zarezerwuj Wycenę</span>
                        </MagneticButton>

                        <div className="flex items-center justify-center sm:justify-start gap-4 text-background/60 w-full sm:w-auto mt-4 sm:mt-0">
                            <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest whitespace-nowrap">Gwarancja Ceny</span>
                            <div className="w-[1px] h-4 bg-background/20 shrink-0"></div>
                            <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest whitespace-nowrap">Szybki Montaż</span>
                        </div>
                    </div>
                </div>

                {/* Trust Bar pushed to bottom right */}
                <div className="reveal-element w-full lg:w-1/3 flex flex-col items-center lg:items-end mb-4 lg:mb-0 mt-8 lg:mt-0">
                    <p className="text-[10px] xl:text-xs font-mono uppercase tracking-widest text-background/40 mb-6 lg:mb-4 lg:text-right w-full text-center">Certyfikowani Wykonawcy</p>
                    <div className="flex flex-wrap justify-center lg:justify-end gap-6 items-center opacity-50 grayscale transition-all duration-500 hover:grayscale-0 hover:opacity-100 cursor-default">
                        <span className="font-heading font-black text-lg xl:text-xl tracking-tighter uppercase text-background">Ruukki</span>
                        <span className="font-heading font-bold text-lg xl:text-xl tracking-wider uppercase text-[#E3000F]">Blachotrapez</span>
                        <span className="font-heading font-black text-lg xl:text-xl uppercase italic text-[#228B22]">Fakro</span>
                        <span className="font-heading font-bold text-lg xl:text-xl tracking-widest uppercase relative border-2 border-[#D81A21] text-background px-1 bg-[#D81A21]">VELUX</span>
                    </div>
                </div>

            </div>
        </section>
    );
}
